import IconButton from '@mui/material/IconButton';
import React from 'react';

import { Formik } from 'formik';

import { useTranslation } from 'next-i18next';

import { AddInGalleryModal } from 'src/components/favorites/AddInGalleryModal';
import Icon from 'src/components/ui/Icon';
import Button from 'src/components/ui/Button';
import ChooseOwnerModal from 'src/components/news/ChooseOwnerModal';
import ConfirmActionModal from 'src/components/ui/ConfirmActionModal';

import { colours } from 'src/constants/colours';

import { logEvent } from 'src/utils/firebase';
import { AnalyticsEvent, TypeOfContent } from 'src/utils/analytics';

import useModalOpenState from 'src/hooks/useModalOpenState';
import useUser from 'src/hooks/useUser';
import useLoginModal from 'src/hooks/useLoginModal';
import useAppRouter from 'src/hooks/useAppRouter';
import useAlerts from 'src/hooks/useAlerts';

import type { Ad, Post, NewsPost } from 'src/types/__generated__/graphql';
import { useRemoveContentFromGalleriesMutation } from 'src/hooks/__generated__/queries';
import { routePathProfileCompany } from 'src/constants/router';
import useApolloCacheUpdate from 'src/hooks/useApolloCacheUpdate';
import { styledComponent } from 'src/utils/styled';
import useDynamicLogEvents from 'src/hooks/useDynamicLogEvents';
import { galleriesQueries } from 'src/constants/galleries';

const StyledIconButton = styledComponent(IconButton)<{
    $isAdPreview: boolean;
    $displayType: 'icon' | 'button' | 'icon-outlined';
}>`
    ${({ $isAdPreview }) =>
        $isAdPreview
            ? `
        position: absolute;
        top: 0.5rem;
        right: 0.5rem;
        z-index: 10;
        background-color: ${colours.grey.G50};

        &:hover {
            background-color: ${colours.grey.G100};
        }
        `
            : `
        width: 3rem;
        padding: 0.75rem 0.25rem;
        svg {
            margin-right: 0.05rem;
        }
    `}

    ${({ $displayType }) =>
        $displayType === 'icon-outlined' &&
        `
            border: 0.0625rem solid ${colours.primary.main};
            height: 2.875rem;
            width: 2.875rem;
        `}
`;

interface ChoseOwnerFormValues {
    userId?: string;
    companyId?: string;
}

export interface ToggleItemInGalleryProps {
    ad?: NonNullable<Pick<Ad, 'isUserFavorite' | 'isUserAffiliatedCompanyFavorite' | 'id'>>;
    post?: Pick<Post, 'isUserFavorite' | 'isUserAffiliatedCompanyFavorite' | 'id'>;
    newsPost?: NonNullable<Pick<NewsPost, 'isUserFavorite' | 'isUserAffiliatedCompanyFavorite' | 'id'>>;
    displayType?: 'icon' | 'button' | 'icon-outlined';
    isAdPreview?: boolean;
}

const ToggleItemInGallery: React.FC<ToggleItemInGalleryProps> = ({
    ad,
    post,
    newsPost,
    displayType = 'icon',
    isAdPreview = false,
}) => {
    const currentUser = useUser();
    const router = useAppRouter();
    const { createAuthenticatedHandler } = useLoginModal();
    const {
        removeFavoritePostInCache,
        removeFavoriteAdInCache,
        removeFavoriteNewsPostInCache,
        decrementNumberOfContentsOnGalleryInCache,
    } = useApolloCacheUpdate();
    const { t } = useTranslation(['ad', 'favorites', 'common']);
    const { addAlert } = useAlerts();
    const { logEventDynamic } = useDynamicLogEvents();

    const { props: chooseOwnerAddModalProps, open: openChooseOwnerAddModal } = useModalOpenState();
    const { props: addModalProps, open: openAddModal } = useModalOpenState();
    const { props: confirmModalProps, open: openConfirmModal } = useModalOpenState();

    const [removeContentFromGalleries, { loading }] = useRemoveContentFromGalleriesMutation({
        onCompleted(data) {
            if (post) {
                removeFavoritePostInCache(post.id);
            }
            if (ad) {
                removeFavoriteAdInCache(ad.id);
            }
            if (newsPost) {
                removeFavoriteNewsPostInCache(newsPost.id);
            }
            for (const gallery of data.removeContentFromGalleries) {
                decrementNumberOfContentsOnGalleryInCache({
                    id: gallery.id,
                    numberOfContents: gallery.numberOfContents,
                });
            }
        },
    });

    const handleOpenRemoveModal = async (ev: React.MouseEvent<HTMLElement>) => {
        ev.stopPropagation();
        ev.preventDefault();
        await createAuthenticatedHandler(() => openConfirmModal());
    };

    const handleOpenAddModal = async (ev: React.MouseEvent<HTMLElement>) => {
        ev.stopPropagation();
        ev.preventDefault();
        await createAuthenticatedHandler(() => {
            if (currentUser?.affiliatedCompany && currentUser.isAdminOfAffiliatedCompany) {
                openChooseOwnerAddModal();
            } else {
                openAddModal();
            }
        });
        logEventDynamic(AnalyticsEvent.AddToFavorites);
    };

    const handleDeleteFromGallery = async () => {
        try {
            const adId = ad?.id;
            const postId = post?.id;
            const newsPostId = newsPost?.id;

            await removeContentFromGalleries({
                variables: {
                    adId,
                    postId,
                    newsPostId,
                    isCompanyGallery: Boolean(router.asPath.includes(routePathProfileCompany)),
                },
                refetchQueries: ['CurrentUser', ...galleriesQueries, 'GalleryContent', 'Gallery'],
                awaitRefetchQueries: true,
            });
            if (adId) {
                logEvent(AnalyticsEvent.RemoveFromFavorites, {
                    AD_ID: adId,
                    TYPE_OF_CONTENT: TypeOfContent.Ad,
                });
            } else if (postId) {
                logEvent(AnalyticsEvent.RemoveFromFavorites, {
                    POST_ID: postId,
                    TYPE_OF_CONTENT: TypeOfContent.Post,
                });
            }
            addAlert({
                type: 'success',
                message: t(adId ? 'favorites:adRemovedFromGallery' : 'favorites:postRemovedFromGallery'),
            });
        } catch (error: unknown) {
            addAlert({
                type: 'error',
                message: String(error),
            });
        }
    };

    let isItemInGallery = ad?.isUserFavorite ?? post?.isUserFavorite ?? newsPost?.isUserFavorite;
    if (router.asPath.includes('profile/company')) {
        isItemInGallery =
            ad?.isUserAffiliatedCompanyFavorite ??
            post?.isUserAffiliatedCompanyFavorite ??
            newsPost?.isUserAffiliatedCompanyFavorite;
    }

    const styledIconButton = (
        <StyledIconButton
            $displayType={displayType}
            $isAdPreview={isAdPreview}
            disabled={loading}
            data-target={isItemInGallery ? 'post-card-actions' : undefined}
            data-target-id={isItemInGallery ? 'post-card-favorites' : undefined}
            onClick={isItemInGallery ? handleOpenRemoveModal : handleOpenAddModal}
            size={isAdPreview ? 'small' : undefined}
        >
            <Icon
                name={isItemInGallery ? 'BookmarkPrimaryActive' : 'BookmarkPrimaryInactive'}
                size={isAdPreview ? '1rem' : undefined}
            />
        </StyledIconButton>
    );

    const styledButton = (
        <Button
            data-target={isItemInGallery ? 'post-card-actions' : undefined}
            data-target-id={isItemInGallery ? 'post-card-favorites' : undefined}
            size="large"
            startIcon={
                isItemInGallery ? <Icon name="BookmarkPrimaryActive" /> : <Icon name="BookmarkPrimaryInactive" />
            }
            variant="outlined"
            onClick={isItemInGallery ? (event) => handleOpenRemoveModal(event) : handleOpenAddModal}
        >
            {isItemInGallery ? t('favorites:deleteFromFavorites') : t('ad:addToFavorites')}
        </Button>
    );

    return (
        <>
            {displayType === 'button' ? styledButton : styledIconButton}
            {/* Formik is used here as it's required by the ChoseOwnerModal */}
            <Formik
                initialValues={
                    {
                        userId: currentUser?.id,
                        companyId: undefined,
                    } as unknown as ChoseOwnerFormValues
                }
                onSubmit={console.log}
                enableReinitialize
            >
                {({ values }) => (
                    <>
                        {chooseOwnerAddModalProps.open && (
                            <ChooseOwnerModal
                                contentType="gallery"
                                openOwnerModal={chooseOwnerAddModalProps.open}
                                onClose={() => {
                                    chooseOwnerAddModalProps.onClose();
                                }}
                                onOwnerChosen={() => {
                                    chooseOwnerAddModalProps.onClose();
                                    openAddModal();
                                }}
                            />
                        )}
                        <AddInGalleryModal
                            {...addModalProps}
                            newsPostId={newsPost?.id}
                            postId={post?.id}
                            adId={ad?.id}
                            userId={values.userId}
                            companyId={values.companyId}
                        />
                    </>
                )}
            </Formik>

            <ConfirmActionModal
                open={confirmModalProps.open}
                title={t('favorites:removeFromGalleryConfirmTitle')}
                description={t('favorites:removeFromGalleryConfirmText')}
                confirm={t('common:confirm')}
                onConfirm={async () => {
                    confirmModalProps.onClose();
                    await handleDeleteFromGallery();
                }}
                onCloseOrCancel={() => confirmModalProps.onClose()}
            />
        </>
    );
};

export default ToggleItemInGallery;
